import React, { useState } from 'react';
import ReceivedPlayerEvents from './ReceivedPlayerEvents';
import AutomationScenarios from './AutomationScenarios';

const headers = () => ({
  'Content-Type': 'application/json',
  'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content
});
const inputClass = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm';
const secondaryClass = 'px-3 py-2 text-sm rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50';
const primaryClass = 'px-3 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50';

async function request(path, method, definition) {
  const response = await fetch(path, {
    method, headers: headers(),
    body: definition ? JSON.stringify({ event_definition: definition }) : undefined
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.errors?.join('. ') || 'Request failed. Please try again.');
  return data;
}

const Dialog = ({ title, onClose, busy, children }) => (
  <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'>
    <section role='dialog' aria-modal='true' aria-labelledby='event-dialog-title'
      className='bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto'>
      <div className='flex items-center justify-between px-5 py-4 border-b border-slate-200'>
        <h2 id='event-dialog-title' className='font-semibold text-slate-900'>{title}</h2>
        <button type='button' aria-label='Close dialog' disabled={busy} onClick={onClose}
          className='text-slate-500 px-2 disabled:opacity-50'>✕</button>
      </div>
      <div className='p-5'>{children}</div>
    </section>
  </div>
);

const EventForm = ({ initial, presets, onSaved, onCancel }) => {
  const [name, setName] = useState(initial?.name || '');
  const [eventType, setEventType] = useState(initial?.event_type || '');
  const [active, setActive] = useState(initial?.active ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const preset = presets.find((item) => item.event_type === eventType.trim());
  const parameterNames = preset ? preset.parameter_names : (initial?.parameter_names || []);

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      const definition = await request(initial ? `/events/${initial.id}` : '/events', initial ? 'PATCH' : 'POST', {
        name, event_type: eventType, active,
        parameter_names: parameterNames
      });
      onSaved(definition);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <Dialog title={initial ? 'Edit event' : 'New event'} onClose={onCancel} busy={saving}>
      <form onSubmit={submit} className='space-y-4'>
        <div>
          <label htmlFor='event-name' className='block text-sm font-medium text-slate-700 mb-1'>Name</label>
          <input autoFocus required id='event-name' value={name} onChange={(e) => setName(e.target.value)}
            className={inputClass} placeholder='e.g. First deposit' disabled={saving} />
        </div>
        <div>
          <label htmlFor='event-type' className='block text-sm font-medium text-slate-700 mb-1'>Event Type</label>
          <select required id='event-type' value={eventType} onChange={(e) => setEventType(e.target.value)}
            className={inputClass} disabled={saving}>
            <option value=''>Choose an event</option>
            {initial && !presets.some((item) => item.event_type === initial.event_type) &&
              <option value={initial.event_type} disabled>{initial.event_type} (legacy)</option>}
            {presets.map((item) => <option key={item.event_type} value={item.event_type}>{item.name}</option>)}
          </select>
          {preset && <p className='text-xs text-slate-500 mt-1'>{preset.event_type}</p>}
        </div>
        <div>
          <label htmlFor='event-parameters' className='block text-sm font-medium text-slate-700 mb-1'>Parameters</label>
          <textarea id='event-parameters' rows={preset ? Math.min(parameterNames.length, 9) : 4}
            value={parameterNames.join('\n')} readOnly
            className={inputClass} disabled={saving} />
          <p className='text-xs text-slate-500 mt-1'>{preset
            ? 'Parameters are filled automatically for this event.'
            : (initial ? 'Select an available event to use automatic parameters.' : 'Choose an event to see its parameters.')}</p>
        </div>
        <label className='flex items-center gap-2 text-sm text-slate-700'>
          <input type='checkbox' checked={active} onChange={(e) => setActive(e.target.checked)} disabled={saving} />
          Active
        </label>
        {error && <p role='alert' className='text-sm text-red-600'>{error}</p>}
        <div className='flex justify-end gap-2 pt-2'>
          <button type='button' onClick={onCancel} disabled={saving} className={secondaryClass}>Cancel</button>
          <button type='submit' disabled={saving} className={primaryClass}>{saving ? 'Saving…' : 'Save event'}</button>
        </div>
      </form>
    </Dialog>
  );
};

const EventTypes = ({ initialEventTypes = [], eventPresets = [], initialReceivedEvents = [], initialListener,
  initialScenarios = [], scenarioSegments = [], scenarioSmsTemplates = [], scenarioEmailTemplates = [], scenarioAttributeGroups = [], scenarioEmailSenders = [] }) => {
  const [events, setEvents] = useState(initialEventTypes);
  const [modal, setModal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const saved = (event) => {
    setEvents((list) => list.some((item) => item.id === event.id)
      ? list.map((item) => item.id === event.id ? event : item) : [event, ...list]);
    setModal(null);
  };

  const remove = async () => {
    if (deleting) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await request(`/events/${modal.event.id}`, 'DELETE');
      setEvents((list) => list.filter((item) => item.id !== modal.event.id));
      setModal(null);
    } catch (e) { setDeleteError(e.message); }
    finally { setDeleting(false); }
  };

  return (
    <div className='max-w-6xl mx-auto px-4 py-6'>
      <div className='flex items-center justify-between gap-4 mb-6'>
        <div>
          <h1 className='text-2xl font-semibold text-slate-900'>Events</h1>
          <p className='text-sm text-slate-500 mt-1'>Event types available for automatic communications.</p>
        </div>
        <button className={primaryClass} onClick={() => setModal({ mode: 'new' })}>New event</button>
      </div>
      <div className='bg-white border border-slate-200 rounded-xl overflow-x-auto'>
        <table className='w-full text-sm text-left'>
          <thead className='bg-slate-50 text-slate-500'>
            <tr>{['Name', 'Event Type', 'Status', 'Parameters', 'Actions'].map((label) =>
              <th key={label} scope='col' className='px-4 py-3 font-medium'>{label}</th>)}</tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {events.map((event) => (
              <tr key={event.id}>
                <td className='px-4 py-3 font-medium text-slate-900 break-words'>
                  <button onClick={() => setModal({ mode: 'edit', event })} className='text-left hover:text-blue-600'>
                    {event.name}
                  </button>
                </td>
                <td className='px-4 py-3 font-mono text-slate-600 break-all'>{event.event_type}</td>
                <td className='px-4 py-3'>
                  <span className={`px-2 py-1 rounded text-xs ${event.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                    {event.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className='px-4 py-3 text-slate-600 break-words'>{event.parameter_names.join(', ') || 'None'}</td>
                <td className='px-4 py-3'>
                  <div className='flex items-center gap-3'>
                    <button onClick={() => setModal({ mode: 'edit', event })}
                      className='text-blue-600 disabled:opacity-50'>Edit</button>
                    <button onClick={() => { setDeleteError(''); setModal({ mode: 'delete', event }); }}
                      className='text-red-600 disabled:opacity-50'>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {events.length === 0 && <tr><td colSpan={5} className='px-4 py-12 text-center text-slate-500'>
              No events yet. Create your first event.
            </td></tr>}
          </tbody>
        </table>
      </div>
      <AutomationScenarios initialScenarios={initialScenarios} events={events} segments={scenarioSegments}
        smsTemplates={scenarioSmsTemplates} emailTemplates={scenarioEmailTemplates} attributeGroups={scenarioAttributeGroups} emailSenders={scenarioEmailSenders} />
      <ReceivedPlayerEvents initialEvents={initialReceivedEvents} initialListener={initialListener} />
      {(modal?.mode === 'new' || modal?.mode === 'edit') &&
        <EventForm initial={modal.event} presets={eventPresets} onSaved={saved} onCancel={() => setModal(null)} />}
      {modal?.mode === 'delete' && <Dialog title='Delete event' onClose={() => setModal(null)} busy={deleting}>
        <p className='text-sm text-slate-700'>Delete “{modal.event.name}”? This cannot be undone.</p>
        {deleteError && <p role='alert' className='text-sm text-red-600 mt-3'>{deleteError}</p>}
        <div className='flex justify-end gap-2 mt-5'>
          <button onClick={() => setModal(null)} disabled={deleting} className={secondaryClass}>Cancel</button>
          <button onClick={remove} disabled={deleting}
            className='px-3 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50'>
            {deleting ? 'Deleting…' : 'Delete event'}
          </button>
        </div>
      </Dialog>}
    </div>
  );
};

export default EventTypes;
